import React, { useState, useEffect } from 'react';
import {
  Card, Table, DatePicker, Select, Button, message,
  Row, Col, Statistic, Tabs
} from 'antd';
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import { Line, Pie, Bar } from '@ant-design/plots';
import api from '../utils/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'days'), dayjs()]);
  const [campaignReport, setCampaignReport] = useState(null);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [accountReport, setAccountReport] = useState([]);
  const [activeTab, setActiveTab] = useState('campaign');

  useEffect(() => {
    fetchReports();
  }, [dateRange]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const startDate = dateRange[0]?.format('YYYY-MM-DD');
      const endDate = dateRange[1]?.format('YYYY-MM-DD');

      // 并行获取报表数据
      const [campaignRes, trendRes, accountRes] = await Promise.all([
        api.get(`/reports/campaign-performance?start_date=${startDate}&end_date=${endDate}`),
        api.get(`/reports/daily-trend?days=30`),
        api.get(`/reports/account-performance?start_date=${startDate}&end_date=${endDate}`)
      ]);

      setCampaignReport(campaignRes.data);
      setDailyTrend(trendRes.data);
      setAccountReport(accountRes.data);
    } catch (error) {
      message.error('获取报表数据失败');
    } finally {
      setLoading(false);
    }
  };

  const campaignColumns = [
    { title: '系列名称', dataIndex: 'name', key: 'name' },
    { title: '状态', dataIndex: 'status', key: 'status' },
    { title: '消耗', dataIndex: 'spend', key: 'spend', render: (v) => `$${v}` },
    { title: '展示', dataIndex: 'impressions', key: 'impressions' },
    { title: '点击', dataIndex: 'clicks', key: 'clicks' },
    { title: '转化', dataIndex: 'conversions', key: 'conversions' },
    { title: 'CTR', dataIndex: 'ctr', key: 'ctr', render: (v) => `${(v * 100).toFixed(2)}%` },
    { title: 'ROAS', dataIndex: 'roas', key: 'roas' },
  ];

  const accountColumns = [
    { title: '账户名称', dataIndex: 'account_name', key: 'account_name' },
    { title: '状态', dataIndex: 'status', key: 'status' },
    { title: '系列数', dataIndex: 'total_campaigns', key: 'total_campaigns' },
    { title: '消耗', dataIndex: 'total_spend', key: 'total_spend', render: (v) => `$${v}` },
    { title: '展示', dataIndex: 'total_impressions', key: 'total_impressions' },
    { title: '点击', dataIndex: 'total_clicks', key: 'total_clicks' },
    { title: '转化', dataIndex: 'total_conversions', key: 'total_conversions' },
    { title: 'CTR', dataIndex: 'avg_ctr', key: 'avg_ctr', render: (v) => `${(v * 100).toFixed(2)}%` },
  ];

  const lineConfig = {
    data: dailyTrend,
    xField: 'date',
    yField: 'spend',
    seriesField: 'type',
    smooth: true,
    animation: {
      appear: {
        animation: 'path-in',
        duration: 5000,
      },
    },
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>数据报表</h2>
        <div style={{ display: 'flex', gap: 16 }}>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchReports} loading={loading}>
            刷新
          </Button>
          <Button icon={<DownloadOutlined />}>
            导出
          </Button>
        </div>
      </div>

      {campaignReport?.summary && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={4}>
            <Card>
              <Statistic title="总系列数" value={campaignReport.summary.total_campaigns} />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic title="总消耗" value={campaignReport.summary.total_spend} prefix="$" />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic title="总展示" value={campaignReport.summary.total_impressions} />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic title="总点击" value={campaignReport.summary.total_clicks} />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic title="总转化" value={campaignReport.summary.total_conversions} />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic title="平均ROAS" value={campaignReport.summary.avg_roas} />
            </Card>
          </Col>
        </Row>
      )}

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="广告系列性能" key="campaign">
          <Card>
            <Table
              columns={campaignColumns}
              dataSource={campaignReport?.campaigns || []}
              rowKey="id"
              loading={loading}
              scroll={{ x: 1200 }}
            />
          </Card>
        </TabPane>

        <TabPane tab="每日趋势" key="trend">
          <Card title="消耗趋势">
            <Line {...lineConfig} />
          </Card>
        </TabPane>

        <TabPane tab="账户性能" key="account">
          <Card>
            <Table
              columns={accountColumns}
              dataSource={accountReport}
              rowKey="account_id"
              loading={loading}
            />
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default Reports;
